import { ParsedUrlQuery } from 'querystring';
import {
  HeroBrandEvolutionProps as HeroBrandEvolutionComponentProps,
  HeroProps as HeroComponentProps,
  PressMediaArticleCardProps,
  PressReleaseCardProps,
  ProductCategoryTilesProps as ProductCategoryTilesComponentProps,
  RaisinUIProviderProps,
} from '@raisin/components-lib';
import { LayoutProps } from '@raisin/cp-layout';
import { DepositProduct } from '@raisin/cp-utils';
import type { ModelPortfolios } from '@raisin/investments-data/api/contentStack/types';
import { FormattedBankAdditionalDetails } from 'utils/bankPageUtils/bankAdditionalDetails';
import { OffersTableConfig } from 'utils/offersTableConfigs/config';
import { Product, ProductType } from '../api/dbs/models';
import { BankDetailsType } from './bank';
import { USProductOffer } from './usProductOffer';

// ===================================================== //
//                       TYPES
// ===================================================== //
export type contentModule = {
  uid: string;
  _content_type_uid: string;
};

export type contentModuleRef = {
  [key: string]: Array<string>;
};

export type DBSProduct = Product;

// ===================================================== //
//                       INTERFACES
// ===================================================== //
export interface ContentProps {
  componentName: string;
  componentData: {
    uid: string;
  } & Record<string, unknown>;
}

export interface PageTemplateProps {
  layout: LayoutProps['layout'];
  modelPortfolios: ModelPortfolios;
  bankPage: {
    isLoginRequired: boolean;
    bank_id: string | null;
    bank_details: BankDetailsType | null;
    bank_offers: DBSProduct[];
    bank_offer_id: string | null;
    highestInterestRate: string;
    titleId: string;
    navigation_settings: {
      hide_main_navigation: boolean;
      hide_footer: boolean;
      hide_footer_links: boolean;
    };
    seo_settings?: LayoutProps['seoSettings'];
  };
  pressReleasePage: {
    uid: string;
    title: string;
    page_settings?: string;
    seo_settings?: LayoutProps['seoSettings'];
    url: string;
  } | null;
  page: {
    uid: string;
    title: string;
    page_type: string;
    page_settings?: string;
    navigation_settings?: {
      hide_main_navigation: boolean;
      hide_footer: boolean;
      hide_footer_links: boolean;
    };
    seo_settings?: LayoutProps['seoSettings'];
    theme_version?: RaisinUIProviderProps['version'];
    url: string;
    _content_type_uid: string;
    publish_details: {
      time: string;
    };
  } | null;
  pressPageData: {
    uid: string;
    title: string;
    url: string;
    _version: number;
    locale: string;
    publish_details: {
      time: string;
    };
    page_settings?: string;
    seo_settings?: LayoutProps['seoSettings'];
    theme_version?: RaisinUIProviderProps['version'];
  } | null;
  headingData: Array<ContentProps>;
  contentData: Array<ContentProps>;
  sidebarData: Array<ContentProps> | [];
  productInformation: Array<ContentProps> | [];
  isLoginRequired: boolean;
  isCorporate: boolean;
  banks: { entries: DepositTakingBankListItem[]; total: number };
  pressReleases: PressReleaseCardProps[];
  mediaArticles: PressMediaArticleCardProps[];
  contentEntries: Record<string, any>;
  usProductOffers: Array<USProductOffer>;
  messages: {
    locale: string;
    locales: string[];
    translations: Record<string, any>;
  };
  locale: string;
}

export interface PressReleasePageProps {
  headingData: ContentProps[];
  contentData: ContentProps[];
  sidebarData: ContentProps[];
  pressPageData: [PageTemplateProps['pressPageData']];
  modelPortfolios?: ModelPortfolios;
}

export interface PageContentProps {
  headingData: ContentProps[];
  productInformation: ContentProps[];
  contentData: ContentProps[];
  sidebarData: ContentProps[];
  modelPortfolios?: ModelPortfolios;
}

export interface BankPageContentProps {
  bankPage: {
    isLoginRequired: boolean;
    bank_id: string | null;
    bank_details: BankDetailsType | null;
    bank_offers: DBSProduct[];
    bank_offer_id: string | null;
    highestInterestRate: string;
    titleId: string;
    navigation_settings: {
      hide_main_navigation: boolean;
      hide_footer: boolean;
      hide_footer_links: boolean;
    };
    seo_settings?: LayoutProps['seoSettings'];
  };
  banks: { entries: DepositTakingBankListItem[]; total: number } | null;
  asPath: string;
  layout: LayoutProps['layout'];
  locale: string;
  messages: Record<string, string>;
}

export interface Offers {
  partner_banks: PartnerBankElement[];
  deposits: Deposit[];
  countries: Country[];
}

export interface Country {
  id: string;
  country_code: string;
  name: string;
  description?: string;
  source_tax?: number;
  source_tax_description?: string;
  credit_rating: string;
  deposit_guarantee_amount: number;
  deposit_guarantee_currency?: string;
  rating_date?: null;
  source_tax_reduced_sme?: number;
  source_tax_reduced?: number;
}
export interface StatutoryDepositGuaranteeScheme {
  country: {
    id: string;
    name: string;
    description: string;
    rating: string;
  };
}
export interface Deposit {
  id: string;
  settlement_day?: string[];
  daily_settlement?: boolean;
  is_rollover_enabled?: boolean;
  has_fixed_investment_amount: boolean;
  is_in_foreign_currency: boolean;
  min_placement_amount: number;
  max_placement_amount: number;
  is_overnight: boolean;
  is_notice_account: boolean;
  is_term_account: boolean;
  is_marketed_as_overnight: boolean;
  product_type: string;
  marketed_as_product_type?: ProductType;
  currency: string;
  nominal_interest_rate: number;
  effective_interest_rate: number;
  prolongation_interest_rate?: number;
  has_fully_online_process: boolean;
  is_flexgeld: boolean;
  is_top_offer: boolean;
  has_auto_prolongation: boolean;
  number_of_terms: number;
  autoprolongation_number_of_terms?: number;
  premature_termination?: boolean;
  interest_payout_scheme: InterestPayoutScheme;
  early_termination_interest_rate?: number;
  early_termination_effective_interest_rate?: number;
  opening_document: Document;
  prolongation_window?: number;
  maturity_window?: number;
  prolongation_document?: Document;
  prolongation_capitalisation?: ProlongationCapitalisation;
  prolongation_enabled: boolean;
  prolongation_option?: boolean;
  source_tax_applicable: boolean;
  source_tax_type: string | null;
  domestic_taxation?: boolean;
  rate_change?: boolean;
  rate_change_date?: Date;
  rate_change_value?: number;
  interest_paid_on_booking?: boolean;
  termination_notice_period?: number;
  partner_bank: DepositPartnerBank;
  cms_content: DepositCMSContent;
  interest_compounding: boolean;
  interest_booking_interval: InterestBookingInterval;
  badge_content_1?: string;
  badge_content_2?: string;
  badge_content_3?: string;
  savings_product_name?: string;
  interest_profit_rate?: string;
  change_interest_profit_rate?: string;
  how_much_profit?: string;
  how_manage_savings?: string;
  withdraw_money?: string;
  additional_information?: string;
  information_required?: string;
  deposit_guarantee?: string;
  raisin_exclusive: boolean;
  special_offer_chip: boolean;
  is_channel_exculsive: boolean;
  is_marketing_leading_rate: boolean;
  is_structured_deposit: boolean;
  is_rate_reducing_soon: boolean;
  payout_period_after_termination?: number;
  availability_notice?: string;
  statutory_deposit_guarantee_scheme: StatutoryDepositGuaranteeScheme;
  is_sustainable_deposit?: boolean;
  is_promotional_offer?: boolean;
  is_contractual_termination_allowed?: boolean;
  termination_interest_policy?: interestPolicies;
  dbs_interest_rates?: {
    interest_rate_effective: string;
    interest_rate_nominal: string;
    effective_date: string;
    upcoming_rates: {
      interest_rate_effective: string;
      interest_rate_nominal: string;
      effective_date: string;
    }[];
  };
  dbs_term?: {
    unit: string;
    period: number;
  };
  pre_contractual_documents?: {
    id: string;
    display_name: string;
    document_type: string;
    url: string;
    version: number;
  }[];
  restricted_to_new_customers?: boolean;
}

export interface DepositCMSContent {
  finanz_test_label?: DepositInsuranceDescription;
  deposit_insurance_description: Partial<DepositInsuranceDescription>;
  description_document: Partial<DescriptionDocument>;
  deposit_insurance_name: Partial<DepositInsuranceDescription>;
  deposit_insurance_logo: Partial<DepositInsuranceDescription>;
  interest_payment_terms?: DepositInsuranceDescription;
  deposit_terms?: DepositInsuranceDescription;
  termination_notice?: DepositInsuranceDescription;
  interest_rate_changes_notice?: DepositInsuranceDescription;
}

export interface DepositInsuranceDescription {
  id: DepositInsuranceDescriptionID;
  type: DepositInsuranceDescriptionType;
  value: string;
}

export enum DepositInsuranceDescriptionID {
  DepositInsuranceDescription = 'deposit_insurance_description',
  DepositInsuranceName = 'deposit_insurance_name',
  DepositInsuranceLogo = 'deposit_insurance_logo',
  DepositTerms = 'deposit_terms',
  FinanzTestLabel = 'finanz_test_label',
  InterestPaymentTerms = 'interest_payment_terms',
  InterestRateChangesNotice = 'interest_rate_changes_notice',
  TerminationNotice = 'termination_notice',
}

export enum DepositInsuranceDescriptionType {
  StringHTML = 'STRING_HTML',
  StringPlain = 'STRING_PLAIN',
}

export interface DescriptionDocument {
  id: DescriptionDocumentID;
  type: DescriptionDocumentType;
  media_type: MediaType;
  url_path: string;
}

export enum DescriptionDocumentID {
  DescriptionDocument = 'description_document',
  LogoURL = 'logo_url',
}

export enum MediaType {
  ApplicationPDF = 'application/pdf',
  ImageJPEG = 'image/jpeg',
  ImagePNG = 'image/png',
}

export enum DescriptionDocumentType {
  Attachment = 'ATTACHMENT',
}

export enum InterestBookingInterval {
  Empty = '',
  Monthly = 'MONTHLY',
  Annually = 'ANNUALLY',
  Quarterly = 'QUARTERLY',
  Maturity = 'MATURITY',
}

export enum InterestPayoutScheme {
  Annual = 'ANNUAL',
  Maturity = 'MATURITY',
  Quarterly = 'QUARTERLY',
}

export enum Document {
  Digit = 'DIGIT',
  None = 'NONE',
  Paper = 'PAPER',
}

export enum OFFER_PRODUCT_TYPES {
  TERM = 'TERM',
  OVERNIGHT = 'OVERNIGHT',
  NOTICE = 'NOTICE',
}

export enum productTypes {
  TERM_DEPOSIT = 'term_deposit',
  OVERNIGHT_DEPOSIT = 'overnight_deposit',
  FLEXGELD = 'flexgeld',
  FORREIGN_CURRENCY = 'foreign_currency',
  NOTICE_ACCOUNT = 'notice_account',
}

export enum promotionTypes {
  RAISIN_INVEST = 'raisin_invest',
  RAISIN_PENSION = 'raisin_pension',
}

export type products = productTypes | promotionTypes;

export enum sortingOptions {
  BANK_NAME = 'bank_name',
  RATING = 'rating',
}

export enum orderOptions {
  DESC = 'desc',
  ASC = 'asc',
}

export enum interestPolicies {
  PRO_RATA = 'PRO_RATA',
  NOT_BOOKED = 'NOT_BOOKED',
}

export interface DepositPartnerBank {
  id: string;
  withholding_tax_possible?: null;
  withholding_tax_possible_sme?: null;
  tax_country?: null;
  rating?: null;
  rating_date?: null;
  is_fiduciary?: null;
}

export enum ProlongationCapitalisation {
  CustomerChoice = 'CUSTOMER_CHOICE',
  Prlcap = 'PRLCAP',
  Prlnocap = 'PRLNOCAP',
}

export interface PartnerBankElement {
  id: string;
  fiduciary: boolean;
  country: string;
  description: string;
  bank_slug: string;
  description_short: string;
  name: string;
  partner_bank_sharia_compliant: boolean;
  cms_content: PartnerBankCMSContent;
  withholding_tax_possible: boolean;
  tax_country: string;
  rating: string;
  is_fiduciary: boolean;
  maximum_deposit_amount_per_business?: number;
  additional_details: FormattedBankAdditionalDetails;
}

export interface PartnerBankCMSContent {
  logo: DescriptionDocument;
}

export interface RiskBanner {
  center_box_body?: string;
  denominator?: number;
  left_box_body?: string;
  numerator?: number;
  right_box_body?: string;
}

export type TabProps = {
  tabId: string;
  tabName: string;
  hint?: string;
  icon?: string;
  rebrandedIcon?: React.ReactElement;
  style: {
    width: string;
    whiteSpace: string;
  };
  content?: React.ReactElement;
  promotionTab?: { title: string; subtitle: string; link: string };
  isRebranded?: boolean;
};

export type BuildTabsProps = {
  loading: boolean;
  offers: Object[];
  isRebranded?: boolean;
  onInvestNowClick: (offer: DBSProduct, isLoggedIn: boolean) => void;
  locale: string;
  onMobileInvestNowClick: (offer: DBSProduct, isLoggedIn: boolean) => void;
  config: OffersTableConfig;
  intl: any;
  t: any;
  ACTIVATED_TABS: products[];
  isLoggedIn: boolean;
  onLoadMoreClickHandler: any;
  onOfferRowTabClickHandler: any;
  onTermFilterChangeHandler: any;
  onCountryFilterChangeHandler: any;
  onInvestmentAmountInputChangeHandler: any;
  onColumnSortChangeHandler: any;
  onCountryRatingChangeHandler: any;
  onCTAClickHandler: any;
  isShuffleProductsFeatureFlagOn: any;
};

export enum PageContentTypes {
  DEPOSITS_OFFERS_TABLE = 'deposits_offers_table',
  PAGES = 'pages',
  PRESS_PAGE = 'press_page',
  PRESS_RELEASE_PAGE = 'press_release_page',
  PRESS_MEDIA_ARTICLE = 'press_media_article_card',
}

export enum ContentTypes {
  DEPOSITS_TAKING_BANK = 'deposits_partner_bank',
  DEPOSITS_PARTNER_COUNTRY = 'deposits_partner_country',
  COUNTRY = 'country_',
  REDIRECTS = 'redirects',
  REDIRECTS_BULK_UPLOAD = 'redirects_bulk_upload',
}

export enum PageType {
  ContentStackPage = 'CONTENT_STACK_PAGE',
  BankPage = 'BANK_PAGE',
}
export type Params = ParsedUrlQuery;

export type BankProduct = Omit<DepositProduct, 'deposit_taking_bank'> & {
  deposit_taking_bank: null | undefined;
  marketed_as_product_type?: ProductType;
};

export interface DepositProducts {
  available_types: Array<string>;
  promoted_products: Array<BankProduct>;
}

export interface DepositTakingBankListItem {
  id: string;
  name: string;
  bank_slug: string;
  description: string;
  bank_logo_url: string;
  branch_country: {
    id: string;
    name: string;
    description: string;
    rating: string;
    source_tax_applicable?: boolean;
    source_tax_description?: null | string;
    source_tax_rates?: {
      flat_basic_rate: string;
      flat_reduced_rate: string;
    };
    source_tax_reduction_process_type?: null | string;
    domestic_taxation?: boolean;
  };
  conditions: {
    investment_amount_min: null | string;
    investment_amount_max: string;
    currency: string | undefined | null;
  };
  address: {
    street: string;
    city: string;
    region: string;
    postal_code: string;
    country: string;
  };
  statutory_deposit_guarantee_scheme: {
    name: string;
    country: {
      id: string;
      name: string;
      description: string;
      rating: string;
    };
    description: string;
    insured_amount: string;
    currency: string;
    logo_url: string;
  };
  voluntary_deposit_guarantee_scheme: {
    name: string | null;
    description: string | null;
    insured_amount: string | null;
    currency: string | null;
    logo_url: string | null;
  };
  shariah_compliant: boolean;
  customer_deposit_limit: string[];
  deposit_products: DepositProducts;
}

export interface ShortcodeCustomMenuProps {
  label: string;
  value: string;
}

export interface BankPageProps {
  preview: boolean;
  layout: LayoutProps['layout'] | null;
  banks: { entries: DepositTakingBankListItem[]; total: number } | null;
  bankPage: PageTemplateProps['bankPage'];
  highestInterestRate: string;
}

export interface GetBankPropsFnParams {
  locale: string;
  bankPaths: {
    bankList: string | null;
    bankId: string | null;
    bankProductId: string | null;
  };
  defaultProps: BankPageProps;
}

export type HeroProps = HeroComponentProps & {
  locale?: string;
};

export interface HeroBrandEvolutionProps extends HeroBrandEvolutionComponentProps {
  locale?: string;
}
export interface ProductCategoryTilesProps extends ProductCategoryTilesComponentProps {
  locale?: string;
}

export * from './sitemap';
export * from './usProductOffer';
